import React, { useEffect, useState } from 'react';
import { Button, Card, Form } from '@/components/ui';
import { RateMode, Vehicle } from '../../../types/Vehicle';
import { rateDisplay } from '../utils/equipmentDisplay';
import { RATE_CLASSES_ENABLED } from '../utils/equipmentPermissions';
import vehicleService, { fieldErrorsOf } from '../../../services/vehicleService';
import VehicleBlueBookRatesSection from '../../vehicles/components/VehicleBlueBookRatesSection';

interface EquipmentRatesTabProps {
  vehicle: Vehicle;
  canEditRate: boolean;
  onUpdate: (vehicle: Vehicle) => void;
  /** Tells the page an unsaved rate is on screen, so leaving asks first (EQ-20). */
  onDirtyChange: (dirty: boolean) => void;
}

/** Rates (EQ-20): read-only, or the section's own Edit with Save and Cancel (V2-074). */
const EquipmentRatesTab: React.FC<EquipmentRatesTabProps> = ({ vehicle, canEditRate, onUpdate, onDirtyChange }) => {
  const initial = vehicle.rate != null ? String(vehicle.rate) : '';
  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRate(initial);
  }, [initial]);

  const dirty = editing && rate !== initial;
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const modeLabel =
    vehicle.rateMode === RateMode.Custom ? 'Custom' : vehicle.rateMode === RateMode.RateClass ? 'Rate Class' : 'Unit Rate';

  const cancel = () => {
    setRate(initial);
    setError(null);
    setEditing(false);
  };

  const save = async () => {
    if (rate.trim() === '' || !Number.isFinite(Number(rate)) || Number(rate) < 0) {
      setError('Rate must be 0 or more');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onUpdate(await vehicleService.setRate(vehicle.id, { rate: Number(rate) }));
      onDirtyChange(false);
      setEditing(false);
    } catch (err) {
      setError(fieldErrorsOf(err, 'rate').rate ?? fieldErrorsOf(err)._form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Card>
        <Card.Header className="tw-flex tw-items-center tw-justify-between tw-gap-2">
          <h2 className="tw-mb-0 tw-text-base tw-font-semibold">Current Rate</h2>
          {canEditRate && !editing && (
            <Button size="sm" variant="outline-secondary" onClick={() => setEditing(true)}>
              <i className="bi bi-pencil tw-mr-1" aria-hidden="true" />
              Edit
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          <dl className="tw-mb-0 tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-3">
            <div>
              <dt className="tw-text-sm tw-font-semibold tw-text-content-secondary">
                {editing ? <label htmlFor="unit-rate" className="tw-mb-0">Rate ($ / h)</label> : 'Rate'}
              </dt>
              <dd className="tw-mb-0 tw-text-sm tw-text-content tw-tabular-nums">
                {editing ? (
                  <>
                    <Form.Control
                      id="unit-rate"
                      inputMode="decimal"
                      className="tw-max-w-[10rem]"
                      value={rate}
                      onChange={(e) => setRate(e.target.value)}
                    />
                    {error && <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-danger" role="alert">{error}</p>}
                  </>
                ) : (
                  rateDisplay(vehicle)
                )}
              </dd>
            </div>
            <div>
              <dt className="tw-text-sm tw-font-semibold tw-text-content-secondary">Rate Type</dt>
              <dd className="tw-mb-0 tw-text-sm tw-text-content">{modeLabel}</dd>
            </div>
          </dl>
        </Card.Body>
        {editing && (
          <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-line tw-px-4 tw-py-3">
            <Button size="sm" variant="outline-secondary" onClick={cancel} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </Card>
      {RATE_CLASSES_ENABLED && <VehicleBlueBookRatesSection vehicle={vehicle} onUpdate={onUpdate} />}
    </div>
  );
};

export default EquipmentRatesTab;
